<template>
    <Card :title="$t('labels.publication')">
        <div class="card-body">
            <div class="row">
                <div class="col-lg-6">
                    <Fieldset :title="$t('labels.images')">
                        <draggable
                            v-model="product.images"
                            class="image-gallery"
                            :item-key="item => item"
                        >
                            <template #item="{ element, index }">
                                <div class="image-item">
                                    <img :src="element" class="image">
                                    <div class="image-actions">
                                        <button v-if="!isSupplier" @click="$refs.ImageGenerationModal.show(element, getAttributesForImageGeneration(), product, integration.language_aliases)" class="image-action-open-image" :title="$t('labels.ai_generate_image')">
                                            <i class="icon-magic-wand"></i>
                                        </button>
                                        <button @click="openImage(element)" class="image-action-open-image" :title="$t('labels.open_image_in_new_tab')">
                                            <i class="icon-eye"></i>
                                        </button>
                                        <button @click="removeImage(element)" :title="$t('labels.remove_image')">
                                            <i class="icon-cross3"></i>
                                        </button>
                                    </div>
                                    <div v-if="!isSupplier" class="drag-handle">
                                        <i class="icon-move"></i>
                                    </div>
                                    <div class="common-button main-image-badge" v-if="!index">{{ $t('labels.main_image') }}</div>
                                    <button class="common-button make-main-btn" v-else @click="makeMainImage(element)">{{ $t('labels.make_main') }}</button>
                                </div>
                            </template>
                        </draggable>
                        <div
                            v-if="!isSupplier"
                            class="drop-zone mb-3"
                            @click="$refs.FileComponent.showFile()"
                            @dragover.prevent
                            @dragenter.prevent
                            @drop.prevent="handleFileDrop"
                        >
                            <i class="icon-cloud-upload fs-24"></i>
                            <div class="mt-2">
                                <p>{{$t('labels.drag_and_drop') }}
                                    <span class="text-primary">
                                        {{ $t('labels.browse') }}
                                    </span>
                                </p>
                            </div>
                        </div>
                        <AppButton v-if="!isSupplier" color="teal" @click="$refs.ImageGenerationFromGroupProductModal.show(product)" class="mb-2" :disabled="product.group_key === null">
                            {{ $t('buttons.generateImageButton') }}
                        </AppButton>
                    </Fieldset>
                    <Fieldset :title="$t('labels.main_information')">
                        <div class="row">
                            <div class="col-lg-6">
                                <FormGroup :label="$t('labels.category')">
                                    <Autocomplete
                                        :model-value="product.category?.uuid"
                                        :url="`/projects/${$route.params.projectUuid}/pim/channels/${$route.params.integrationUuid}/categories`"
                                        @update:model-value="changeCategory"
                                        :selected-value="getAvailableTranslations(product.category?.names)"
                                    >
                                        <template #optionContent>
                                            {{ getAvailableTranslations(product.category?.names) ?? $t('labels.empty') }}
                                        </template>
                                        <template #optionItem="{option, index}">
                                            {{ getAvailableTranslations(option.names) }}
                                        </template>
                                    </Autocomplete>
                                </FormGroup>
                            </div>
                            <div class="col-lg-6">
                                <FormGroup :label="$t('labels.sku')">
                                    <Input v-model="product.sku"/>
                                </FormGroup>
                            </div>
                            <div class="col-lg-4">
                                <FormGroup :label="$t('labels.measurement')">
                                    <Input v-model="product.measurement"/>
                                </FormGroup>
                            </div>
                            <div class="col-lg-4">
                                <FormGroup :label="$t('labels.manufacturer')">
                                    <Input v-model="product.manufacturer"/>
                                </FormGroup>
                            </div>
                            <div class="col-lg-4">
                                <FormGroup :label="$t('labels.balance')">
                                    <Input v-model="product.balance" :disabled="integration?.product_settings?.fields_for_update.includes('balance') && product.is_auto_balance_update_enabled" type="number"/>
                                </FormGroup>
                            </div>
                            <div class="col-lg-12">
                                <FormGroup :label="$t('labels.tags')">
                                    <MultiAutoComplete
                                        url="/integration-product-tags"
                                        v-model="product.tags"
                                        :can-be-empty="true"
                                        :query-params="{
                                            orders: [{column: 'name', dir: 'asc'}],
                                        }"
                                    />
                                </FormGroup>
                            </div>
                        </div>
                    </Fieldset>
                    <Fieldset :title="$t('labels.filters')">
                        <template v-if="allFilters.length">
                            <div class="row">
                                <div class="col-lg-4" v-for="filter in allFilters" :key="filter.uuid">
                                    <CatalogAttributeFormGroup :attribute="filter" :integration="integration" :category="product.category" :model-value="selectedFilters[filter.uuid]?.items || []" @update:model-value="selectFilter(filter, $event)" />
                                </div>
                            </div>
                            <div v-if="!isSupplier" class="mb-3">
                                <AppButton color="teal" @click="$refs.FilterSubstitutionModal.show(allFilters, selectedFilters)">
                                    {{ $t('buttons.auto_determine') }}
                                </AppButton>
                            </div>
                        </template>
                        <template v-else>
                            <AlertComponent type="info">{{ $t('alerts.no_filters_available') }}</AlertComponent>
                        </template>
                    </Fieldset>
                    <Fieldset :title="$t('labels.prices')">
                        <div class="row">
                            <div class="col-lg-4">
                                <FormGroup :label="$t('labels.price')">
                                    <Input v-model="product.price" :disabled="integration?.product_settings?.fields_for_update.includes('price') && product.is_auto_price_update_enabled" type="number"/>
                                </FormGroup>
                            </div>
                            <div class="col-lg-4">
                                <FormGroup :label="$t('labels.sale_price')">
                                    <Input v-model="product.sale_price" :disabled="integration?.product_settings?.fields_for_update.includes('salePrice') && product.is_auto_price_update_enabled" type="number"/>
                                </FormGroup>
                            </div>
                            <div class="col-lg-4">
                                <FormGroup :label="$t('labels.promo_price')">
                                    <Input v-model="product.promo_price" :disabled="integration?.product_settings?.fields_for_update.includes('promoPrice') && product.is_auto_price_update_enabled" type="number"/>
                                </FormGroup>
                            </div>
                        </div>
                    </Fieldset>
                    <Fieldset :title="$t('labels.identifiers')">
                        <div class="row">
                            <div class="col-md-4">
                                <FormGroup :label="`${$t('labels.external_male')} (${$t('labels.main')})`">
                                    <Input v-model="product.source_id"/>
                                </FormGroup>
                            </div>
                            <div class="col-md-4">
                                <FormGroup :label="`${$t('labels.external_male')} (${$t('labels.secondary')})`">
                                    <Input v-model="product.second_source_id"/>
                                </FormGroup>
                            </div>
                            <div class="col-md-4">
                                <FormGroup :label="$t('labels.group_id')">
                                    <Input v-model="product.group_key"/>
                                </FormGroup>
                            </div>
                            <div class="col-md-12">
                                <FormGroup :label="$t('labels.internal_male')">
                                    <InputGroup>
                                        <Autocomplete
                                            url="/catalog/product-items"
                                            :model-value="product.internal_product?.uuid"
                                            :selected-value="product.internal_product?.name"
                                            :can-be-empty="true"
                                            @update:model-value="product.internal_product = $event"
                                        />
                                        <template #append>
                                            <div class="d-flex gap-2">
                                                <router-link :class="['btn btn-link', product.internal_product?.uuid ? '' : 'disabled']" :to="{
                                                    name: 'EditProductPage',
                                                    params: {
                                                        productUuid: product.internal_product?.uuid ?? '#'
                                                    }}"
                                                >
                                                    {{ $t('buttons.go_over') }}
                                                </router-link>
                                                <AppButton
                                                    color="teal"
                                                    @click="$refs.ConnectExternalProductModal.show([prepareProductData(product)])"
                                                    :disabled="product.internal_product?.uuid"
                                                >
                                                    {{ $t('buttons.connect') }}
                                                </AppButton>
                                            </div>
                                        </template>
                                    </InputGroup>
                                </FormGroup>
                            </div>
                        </div>
                    </Fieldset>
                    <Fieldset :title="$t('labels.settings')">
                        <div class="row">
                            <div class="col-lg-4">
                                <FormGroup :label="$t('labels.exportEnabled')">
                                    <AppCheckbox v-model="product.export_enabled"/>
                                </FormGroup>
                            </div>
                            <div class="col-lg-4">
                                <FormGroup :label="$t('labels.auto_price_update_enabled')">
                                    <AppCheckbox v-model="product.is_auto_price_update_enabled"/>
                                </FormGroup>
                            </div>
                            <div class="col-lg-4">
                                <FormGroup :label="$t('labels.auto_balance_update_enabled')">
                                    <AppCheckbox v-model="product.is_auto_balance_update_enabled"/>
                                </FormGroup>
                            </div>
                        </div>
                    </Fieldset>
                </div>
                <div class="col-lg-6">
                    <Fieldset :title="$t('labels.lang_content')">
                        <NavTab class="nav-tabs-highlight">
                            <NavTabItem v-for="(language, index) in integration.language_aliases" :key="language" :href="`#${language}`" :ref="el => itemRefs[index] = el" @click="selectedLanguageAlias = language">{{ $t(`languages.${language}`) }}</NavTabItem>
                            <template #content>
                                <NavTabPane v-for="language in integration.language_aliases" :id="`${language}`">
                                    <FormGroup :label="$t('labels.name')">
                                        <Input v-model="product.names[language]"/>
                                    </FormGroup>
                                    <FormGroup :label="$t('labels.descriptions')">
                                        <InputTextArea v-model="product.descriptions[language]"/>
                                    </FormGroup>
                                    <FormGroup :label="$t('labels.seos')">
                                        <Tags v-model="product.seos[language]" :disabledTags="categoryTags[language]"/>
                                    </FormGroup>
                                </NavTabPane>
                            </template>
                        </NavTab>
                        <div v-if="!isSupplier" class="d-flex gap-2">
                            <AppButton color="teal" @click="$refs.GenerateContentWithAi.show()">
                                {{ $t('aiGeneration.generateButton') }}
                            </AppButton>
                            <AppButton color="teal" @click="$refs.ContentTranslationModal.show()">
                                {{ $t('aiGeneration.translateContent') }}
                            </AppButton>
                        </div>
                    </Fieldset>
                </div>
            </div>
        </div>
        <div v-if="!isSupplier" class="card-footer d-flex justify-content-end flex-wrap gap-2">
            <AppButton color="indigo" @click="toggleUpdate">
                <span>{{ $t('buttons.save') }}</span>
            </AppButton>
            <AppButton color="teal" @click="$refs.DuplicateProductModal.show(product)">
                {{ $t('buttons.duplicate') }}
            </AppButton>
            <AppButton color="light" @click="deleteProduct">
                <span>{{ $t('buttons.delete') }}</span>
            </AppButton>
        </div>
    </Card>
    <ProductGroupTable v-if="groupKey" :group-key="groupKey" :category="product.category" :integration="integration"/>
    <FileComponent ref="FileComponent" @fileIsSelected="selectFile" :accept="['image/*']"/>
    <GenerateContentWithAi
        ref="GenerateContentWithAi"
        :product="product"
        :integration-service="integration.service"
        :integration-languages="integration.language_aliases"
        :product-filters="getProductFilters()"
        @content-generated="(newProduct) => product = newProduct"
    />
    <ContentTranslationModal
        ref="ContentTranslationModal"
        :languages-aliases="integration.language_aliases"
        :product="product"
        :selected-language-alias="selectedLanguageAlias"
        @product-updated="product = $event"
    />
    <DuplicateProductModal
        ref="DuplicateProductModal"
        :integration="integration"
    />
    <ProductsInAnotherIntegrationsModal
        ref="ProductsInAnotherIntegrationsModal"
    />
    <FilterSubstitutionModal
        ref="FilterSubstitutionModal"
        :get-available-translations="getAvailableTranslations"
        :select-filter="selectFilter"
        :product="product"
        :integration="integration"
        :main-language-alias="mainLanguageAlias"
        @on-substitute="substituteItem"
    />
    <ConnectExternalProductModal
        ref="ConnectExternalProductModal"
        :integration="integration"
        @update:table="fetchProduct"
    />
    <ImageGenerationModal
        ref="ImageGenerationModal"
        @saveGeneratedImage="selectFile"
    />
    <ImageGenerationFromGroupProductModal
        ref="ImageGenerationFromGroupProductModal"
        @saveGeneratedImage="selectFile"
    />
</template>

<script>
import Card from "../../../../../../components/new/CardComponent.vue";
import Input from "../../../../../../components/new/form/Input.vue";
import NavTab from "../../../../../components/tabs/NavTab.vue";
import NavTabItem from "../../../../../components/tabs/NavTabItem.vue";
import NavTabPane from "../../../../../components/tabs/NavTabPane.vue";
import FormGroup from "../../../../../../components/new/form/group/FormGroup.vue";
import InputTextArea from "../../../../../../components/new/form/InputTextArea.vue";
import Tags from "../../../../../../components/new/form/Tags.vue";
import AppButton from "../../../../../../components/new/button/AppButton.vue";
import Autocomplete from "../../../../../../components/new/form/autocomplete/AppAutoComplete.vue";
import Fieldset from "../../../../../../components/new/AppFieldset.vue";
import draggable from 'vuedraggable';
import FileComponent from '../../../../../../components/new/file/AppFile.vue';
import InputGroup from "../../../../../../components/new/form/InputGroup.vue";
import ProductGroupTable from "../../ProductGroupTable.vue";
import GenerateContentWithAi from "../../../modals/GenerateContentWithAi.vue";
import AppCheckbox from "../../../../../../components/new/form/checkbox/AppCheckbox.vue";
import MultiAutoComplete from "../../../../../../components/new/form/autocomplete/MultiAutoComplete.vue";
import ContentTranslationModal from "../../products/edit/modals/ContentTranslationModal.vue";
import DuplicateProductModal from "./modals/DuplicateProductModal.vue";
import ProductsInAnotherIntegrationsModal from "./modals/ProductsInAnotherIntegrationsModal.vue";
import FilterSubstitutionModal from "./modals/FilterSubstitutionModal.vue";
import AlertComponent from "../../../../../../components/AlertComponent.vue";
import ConnectExternalProductModal from "../../../modals/ConnectExternalProductModal.vue";
import ImageGenerationModal from "./modals/imageGeneration/ImageGenerationModal.vue";
import ImageGenerationFromGroupProductModal from "./modals/imageGenerationFromGroupProduct/ImageGenerationFromGroupProductModal.vue";
import CatalogAttributeFormGroup from "./CatalogAttributeFormGroup.vue";
import { IntegrationServiceEnum } from "../../../../../enums/IntegrationServiceEnum";

import { httpClient } from "../../../../../../services/HttpClient";
import MessageNotificator from "../../../../../../services/MessageNotificator";
import lodash from 'lodash';

export default {
    name: "EditProductCard",
    components: {
        Card, Input, NavTab, NavTabItem, NavTabPane, FormGroup, InputTextArea, Tags, AppButton, Autocomplete, Fieldset, draggable, FileComponent, InputGroup,
        ProductGroupTable, GenerateContentWithAi, MultiAutoComplete, AppCheckbox, ContentTranslationModal,
        DuplicateProductModal, ProductsInAnotherIntegrationsModal, FilterSubstitutionModal, AlertComponent, ConnectExternalProductModal, ImageGenerationModal,
        ImageGenerationFromGroupProductModal, CatalogAttributeFormGroup
    },
    props: {
        integration: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            product: {
                names: {},
                descriptions: {},
                seos: {},
                sku: null,
                category: null,
                price: null,
                promo_price: null,
                tags: [],
                is_auto_price_update_enabled: false,
                is_auto_balance_update_enabled: false,
                filters: [],
            },
            originalProduct: null,
            itemRefs: [],
            groupKey: null,
            allFilters: [],
            selectedFilters: {},
            selectedLanguageAlias: null
        }
    },
    methods: {
        substituteItem(substitutedFilters) {
            Object.entries(substitutedFilters).forEach(([filterUuid, filterData]) => {
                this.selectedFilters[filterUuid] = filterData;
            });
        },
        getProductFilters() {
            const productFilterUuids = this.product.filters.map(filter => filter.uuid);
            return this.allFilters.filter(filter => productFilterUuids.includes(filter.uuid));
        },
        selectFilter(filter, filterItem) {
            const filterUuid = filter.uuid;

            if (!filterItem || filterItem.length === 0) {
                delete this.selectedFilters[filterUuid];
                return;
            }

            if (filter.is_multiple) {
                let items = this.selectedFilters[filterUuid]?.items || [];

                if (!items.find(item => item.uuid === filterItem.uuid)) {
                    items = filterItem;
                }

                this.selectedFilters[filterUuid] = {
                    items: items
                };
            } else {
                this.selectedFilters[filterUuid] = {
                    items: filterItem
                };
            }
        },
        getAvailableTranslations(objectWithTranslations) {
            if (!objectWithTranslations) {
                return null
            }
            return objectWithTranslations[this.mainLanguageAlias] ?? Object.values(objectWithTranslations)[0];
        },
        async fetchProduct() {
            await httpClient.get(`/integrations/products/${this.$route.params.integrationProductUuid}`, {
                extra_fields: ['tags'],
                extra: 1
            }).then((response) => {
                this.product = response.data.data
                this.originalProduct = lodash.cloneDeep(this.product)

                this.selectedFilters = this.groupFilterItemsByFilters(this.product.filters)

                this.groupKey = this.product.group_key
            })
        },
        groupFilterItemsByFilters(filters) {
            let filtersByFilterUuid = {};

            for (let filter of filters) {
                if (!filtersByFilterUuid[filter.uuid]) {
                    filtersByFilterUuid[filter.uuid] = {
                        items: [{
                            ...filter.item,
                            name: filter.item.names[this.mainLanguageAlias]
                        }]
                    };
                } else {
                    filtersByFilterUuid[filter.uuid].items.push({
                        ...filter.item,
                        name: filter.item.names[this.mainLanguageAlias]
                    });
                }
            }
            return filtersByFilterUuid;
        },
        prepareProductRequest(product = this.product, selectedFilters = this.selectedFilters) {
            let request = {
                images: product.images,
                measurement: product.measurement,
                manufacturer_name: product.manufacturer,
                source_id: product.source_id ?? null,
                second_source_id: product.second_source_id ?? null,
                filter_item_uuids: Object.values(selectedFilters).reduce((acc, filter) => {
                    acc.push(...filter.items.map(item => item.uuid));

                    return acc;
                }, []),
                group_key: product.group_key,
                export_enabled: product.export_enabled,
                tag_uuids: product.tags.map(tag => tag.uuid),
                is_auto_price_update_enabled: product.is_auto_price_update_enabled,
                is_auto_balance_update_enabled: product.is_auto_balance_update_enabled,
                product_item_uuid: product.internal_product?.uuid ?? null,
            };

            if (Object.keys(product.names).length > 0) {
                request.names = product.names
            }

            if (Object.keys(product.descriptions).length > 0) {
                request.descriptions = product.descriptions
            }

            if (Object.keys(product.seos).length > 0) {
                request.seos = product.seos
            }

            if (product.category !== null) {
                request.integration_category_uuid = product.category.uuid
            }

            if (product.sku) {
                request.sku = product.sku
            }

            if (!product.is_auto_price_update_enabled || !this.integration?.product_settings?.fields_for_update.includes('price')) {
                request.price = product.price
            }

            if (!product.is_auto_price_update_enabled || !this.integration?.product_settings?.fields_for_update.includes('salePrice')) {
                request.sale_price = product.sale_price
            }

            if (!product.is_auto_price_update_enabled || !this.integration?.product_settings?.fields_for_update.includes('promoPrice')) {
                request.promo_price = product.promo_price
            }

            if (!product.is_auto_balance_update_enabled || !this.integration?.product_settings?.fields_for_update.includes('balance')) {
                request.balance = product.balance
            }

            return request;
        },
        async fetchProductsFromAnotherIntegration() {
            let products = [];

            await httpClient.get('/integrations/products', {
                product_item_uuids: [this.product.internal_product.uuid],
                ignore_uuids: [this.product.uuid],
                extra: 1,
                limit: 0
            }).then((response) => {
                products = response.data.data;
            });

            return products;
        },
        async toggleUpdate() {
            let request = this.prepareProductRequest();

            if (this.product.internal_product) {
                let anotherIntegrationProducts = await this.fetchProductsFromAnotherIntegration();

                const ignoredFields = ['integration_category_uuid', 'filter_item_uuids', 'source_id', 'second_source_id'];

                const originalSelectedFilters = this.groupFilterItemsByFilters(this.originalProduct.filters);
                const originalRequest = this.prepareProductRequest(this.originalProduct, originalSelectedFilters);

                const requestClean = lodash.omit(request, ignoredFields);
                const originalRequestClean = lodash.omit(originalRequest, ignoredFields);

                const hasMeaningfulChanges = !lodash.isEqual(requestClean, originalRequestClean);

                if (anotherIntegrationProducts.length > 0 && hasMeaningfulChanges) {
                    let productsInAnotherIntegrations = anotherIntegrationProducts.map(product => ({
                        integrationName: product.integration.name,
                        integrationUuid: product.integration.uuid,
                        productUuid: product.uuid,
                        productName: product.names?.uk || product.names?.ru || product.sku || product.uuid,
                        productSku: product.sku
                    }));

                    this.$refs.ProductsInAnotherIntegrationsModal.show(productsInAnotherIntegrations, request);
                }
            }

            httpClient.patch(`/integrations/products/${this.$route.params.integrationProductUuid}`, request).then((response) => {
                this.product = response.data.data
                this.originalProduct = lodash.cloneDeep(this.product)
                this.selectedFilters = this.groupFilterItemsByFilters(this.product.filters)
                this.groupKey = this.product.group_key
                new MessageNotificator().success(this.$t("alerts.success"))
            })
        },
        deleteProduct() {
            new MessageNotificator().confirm(this.$t('alerts.confirm_delete', {
                value: this.$t('labels.product')
            }), () => {
                httpClient.delete(`/integrations/products/${this.$route.params.integrationProductUuid}`).then((response) => {
                    new MessageNotificator().success(this.$t("alerts.success"))
                    this.$router.go(-1)
                })
            })
        },
        openImage(imageUrl) {
            window.open(imageUrl, '_blank');
        },
        makeMainImage(imageUrl) {
            const currentIndex = this.product.images.indexOf(imageUrl);
            if (currentIndex !== -1) {
                this.product.images.splice(currentIndex, 1);
                this.product.images.unshift(imageUrl);
            }
        },
        removeImage(imageUrl) {
            this.product.images = this.product.images.filter(image => image !== imageUrl);
        },
        async selectFile(files) {
            let responseData = [];
            let uploadPromises = Array.from(files).map((file) => {
                let requestFile = new FormData();
                requestFile.append('file', file);
                requestFile.append('type', 'product-item');
                return httpClient.post('/file/files', requestFile);
            });

            let responses = await Promise.all(uploadPromises);
            responseData = responses.map(response => response.data.data);

            this.product.images.push(...responseData.map(file => file.url));
        },
        handleFileDrop(event) {
            const files = event.dataTransfer.files;
            if (files.length > 0) {
                this.selectFile(files);
            }
        },
        changeCategory(category) {
            const applyCategory = () => {
                this.selectedFilters = {}
                this.product.category = category
                this.fetchAllFilters()
            }

            if (Object.keys(this.selectedFilters).length > 0) {
                new MessageNotificator().confirm(this.$t('alerts.change_category'), applyCategory)
            } else {
                applyCategory()
            }
        },
        async fetchAllFilters() {
            if (!this.product.category?.uuid) {
                this.allFilters = [];
                return;
            }
            const limit = 100;
            const filters = [];

            const firstResponse = await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters`, {
                integration_category_uuids: [this.product.category?.uuid],
                extra_fields: ['defaultItems'],
                page: 1,
                limit
            });
            filters.push(...firstResponse.data.data);

            const totalPages = Math.ceil(firstResponse.data.meta.total / limit);
            for (let page = 2; page <= totalPages; page++) {
                const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/pim/channels/${this.$route.params.integrationUuid}/filters`, {
                    integration_category_uuids: [this.product.category?.uuid],
                    extra_fields: ['defaultItems'],
                    page,
                    limit
                });
                filters.push(...response.data.data);
            }

            this.allFilters = this.sortFiltersByName(filters);
        },
        sortFiltersByName(filters) {
            return filters.sort((a, b) => {
                const nameA = this.getAvailableTranslations(a.names)?.toLowerCase() || '';
                const nameB = this.getAvailableTranslations(b.names)?.toLowerCase() || '';

                if (nameA < nameB) {
                    return -1;
                }
                if (nameA > nameB) {
                    return 1;
                }
                return 0;
            });
        },
        prepareProductData(productData) {
            let data = {
                id: productData.id,
                uuid: productData.uuid,
                source_id: productData.source_id,
                second_source_id: productData.second_source_id,
                picture_url: productData.images?.[0] || null,
                images: productData.images || [],
                manufacturer_name: productData.manufacture,
                measurement: productData.measurement,
                product: productData.internal_product,
                name: productData.names[this.mainLanguageAlias] || Object.values(productData.names)[0],
                names: productData.names,
                descriptions: productData.descriptions,
                seo: productData.seos,
                sku: productData.sku,
                price: productData.price,
                sale_price: productData.sale_price,
                promo_price: productData.promo_price,
                balance: productData.balance,
                group_key: productData.group_key,
                export_enabled: productData.export_enabled,
                is_auto_price_update_enabled: productData.is_auto_price_update_enabled,
                is_auto_balance_update_enabled: productData.is_auto_balance_update_enabled,
                category: productData.category,
                tags: productData.tags,
                internalProduct: null
            }

            return data
        },
        getAttributesForImageGeneration() {
            let attributes = [];

            for (let attribute of this.allFilters) {
                if (this.selectedFilters[attribute.uuid] === undefined) continue;

                let attributeValues = this.selectedFilters[attribute.uuid].items.map(item => item.name).join(', ')

                attributes.push({
                    attributeUuid: attribute.uuid,
                    attributeName: attribute.name,
                    attributeItemNames: attributeValues
                })
            }

            return attributes;
        }
    },
    computed: {
        isSupplier() {
            return this.integration.service === IntegrationServiceEnum.SUPPLIER
        },
        mainLanguageAlias() {
            return this.integration.language_aliases[0]
        },
        categoryTags() {
            if (!this.product?.category?.seo_keywords) {
                return {}
            }
            return Object.entries(this.product.category.seo_keywords).reduce((acc, [languageAlias, tags]) => {
                acc[languageAlias] = tags.split(', ').map((tag) => ({
                    tag: tag,
                    reason: translate("labels.category")
                }))

                return acc
            }, {})
        },
    },
    updated() {
        this.itemRefs = []
    },
    async mounted() {
        await this.fetchProduct()
        this.fetchAllFilters()

        this.selectedLanguageAlias = this.integration.language_aliases[0]
        this.itemRefs[0].click()
    },
    watch: {
        '$route'(to, from) {
            if (to.params.integrationProductUuid !== from.params.integrationProductUuid) {
                this.fetchProduct()
            }
        }
    }
}
</script>
<style scoped>
.image-gallery {
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
    margin-bottom: 15px;
}
.image-item {
    position: relative;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    overflow: hidden;
}

.image-actions {
    position: absolute;
    top: 5px;
    right: 5px;
    display: flex;
    gap: 5px;
    transition: opacity 0.2s ease-in-out;
}
.image-action-open-image {
    opacity: 0;
}
.image-item:hover .image-actions .image-action-open-image {
    opacity: 1;
}
.image-actions button {
    width: 24px;
    height: 24px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.8);
    border: none;
    border-radius: 50%;
    font-size: 12px;
}
button:focus {
    outline: none;
}
.common-button {
    position: absolute;
    bottom: 5px;
    right: 5px;
    font-size: 10px;
    padding: 2px 6px;
    border-radius: 3px;
}
.main-image-badge {
    background-color: rgba(13, 110, 253, 0.9);
    color: white;
    z-index: 1;
}
.make-main-btn {
    background-color: rgba(255, 255, 255, 0.8);
    border: none;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
}
.image-item:hover .make-main-btn {
    opacity: 1;
}
.image-item:active {
    cursor: grabbing;
}
.drag-handle {
    position: absolute;
    bottom: 5px;
    left: 5px;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.8);
    border-radius: 50%;
    cursor: move;
    font-size: 12px;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
}
.image-item:hover .drag-handle {
    opacity: 1;
}
.image {
    width: 150px;
    height: 150px;
    object-fit: contain;
    background-color: #f0f0f0;
}
.drop-zone {
    border: 2px dashed #dee2e6;
    border-radius: 5px;
    padding: 20px;
    text-align: center;
    background-color: #f8f9fa;
    cursor: pointer;
    transition: background-color 0.3s;
}
.drop-zone:hover {
    background-color: #f2f1f0;
}
.message > span > i {
    font-size: 24px;
}
.modal-footer:not([class*=bg-]) {
    padding-top: 20px;
}
.fs-24 {
    font-size: 24px;
}
.gap-2 {
    gap: 0.5rem;
}
</style>
